import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Save, X, Zap, Wind, Shield, Cpu } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EFFECT_TYPES = [
  { value: 'double_points', label: '2x Points' },
  { value: 'flat_bonus', label: 'Flat Bonus (+25)' },
  { value: 'negate_dnf', label: 'Negate DNF' },
  { value: 'halve_negative', label: 'Halve Negatives' },
  { value: 'bonus_50pct', label: '+50% Points' },
];

const ICON_OPTIONS = ['zap', 'wind', 'shield', 'hard-hat', 'cpu'];

const ICON_MAP = { zap: Zap, wind: Wind, shield: Shield, cpu: Cpu };

export default function BoostsTab() {
  const [boosts, setBoosts] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [creating, setCreating] = useState(false);

  useEffect(() => { fetchBoosts(); }, []);

  const fetchBoosts = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/boosts`, { withCredentials: true });
      setBoosts(res.data);
    } catch (e) { toast.error('Failed to load boosts'); }
  };

  const startEdit = (boost) => {
    setEditingId(boost.boost_id);
    setForm({ ...boost });
    setCreating(false);
  };

  const startCreate = () => {
    setCreating(true);
    setEditingId(null);
    setForm({ name: '', league: 'racing', price: 10000, effect_type: 'flat_bonus', description: '', icon: 'zap' });
  };

  const cancel = () => {
    setEditingId(null);
    setCreating(false);
    setForm({});
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/boosts/${editingId}`, form, { withCredentials: true });
      toast.success('Boost updated');
      setEditingId(null);
      fetchBoosts();
    } catch (e) { toast.error('Failed to update boost'); }
  };

  const saveCreate = async () => {
    if (!form.name?.trim()) { toast.error('Name is required'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/admin/boosts`, form, { withCredentials: true });
      toast.success('Boost created');
      setCreating(false);
      setForm({});
      fetchBoosts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create boost'); }
  };

  const deleteBoost = async (boost_id, name) => {
    if (!window.confirm(`Delete boost "${name}"? Players who own this boost will keep it, but it won't be available for purchase.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/boosts/${boost_id}`, { withCredentials: true });
      toast.success('Boost deleted');
      fetchBoosts();
    } catch (e) { toast.error('Failed to delete boost'); }
  };

  const racingBoosts = boosts.filter(b => b.league === 'racing');
  const motoBoosts = boosts.filter(b => b.league === 'moto');

  const renderForm = (isNew) => (
    <div className="bg-zinc-800 border border-zinc-600 p-4 mb-4 space-y-3" data-testid={isNew ? 'boost-create-form' : 'boost-edit-form'}>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-zinc-400 mb-1">Name</label>
          <input
            data-testid="boost-name-input"
            type="text" value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-white text-sm focus:border-primary focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs text-zinc-400 mb-1">Price (Credits)</label>
          <input
            data-testid="boost-price-input"
            type="number" value={form.price || 0} onChange={e => setForm({ ...form, price: parseInt(e.target.value) || 0 })}
            className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-white text-sm focus:border-primary focus:outline-none"
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-xs text-zinc-400 mb-1">League</label>
          <select
            data-testid="boost-league-select"
            value={form.league || 'racing'} onChange={e => setForm({ ...form, league: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-white text-sm focus:border-primary focus:outline-none"
          >
            <option value="racing">Racing</option>
            <option value="moto">Moto</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-zinc-400 mb-1">Effect Type</label>
          <select
            data-testid="boost-effect-select"
            value={form.effect_type || 'flat_bonus'} onChange={e => setForm({ ...form, effect_type: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-white text-sm focus:border-primary focus:outline-none"
          >
            {EFFECT_TYPES.map(et => (
              <option key={et.value} value={et.value}>{et.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-zinc-400 mb-1">Icon</label>
          <select
            data-testid="boost-icon-select"
            value={form.icon || 'zap'} onChange={e => setForm({ ...form, icon: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-white text-sm focus:border-primary focus:outline-none"
          >
            {ICON_OPTIONS.map(ic => (
              <option key={ic} value={ic}>{ic}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs text-zinc-400 mb-1">Description</label>
        <input
          data-testid="boost-desc-input"
          type="text" value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })}
          className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-white text-sm focus:border-primary focus:outline-none"
          placeholder="What does this boost do?"
        />
      </div>
      <div className="flex gap-2">
        <button onClick={isNew ? saveCreate : saveEdit} className="flex items-center gap-1 bg-primary hover:bg-primary/80 text-white px-4 py-2 text-sm font-bold" data-testid="boost-save-btn">
          <Save size={14} /> {isNew ? 'Create' : 'Save'}
        </button>
        <button onClick={cancel} className="flex items-center gap-1 bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-2 text-sm" data-testid="boost-cancel-btn">
          <X size={14} /> Cancel
        </button>
      </div>
    </div>
  );

  const renderBoostRow = (boost) => {
    const IconComp = ICON_MAP[boost.icon] || Zap;
    const effectLabel = EFFECT_TYPES.find(e => e.value === boost.effect_type)?.label || boost.effect_type;
    const isEditing = editingId === boost.boost_id;

    if (isEditing) return <div key={boost.boost_id}>{renderForm(false)}</div>;

    return (
      <div key={boost.boost_id} className="flex items-center justify-between p-3 border border-zinc-800 hover:border-zinc-600 transition-colors" data-testid={`boost-row-${boost.boost_id}`}>
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 bg-primary/20 flex items-center justify-center">
            <IconComp className="text-primary" size={20} />
          </div>
          <div className="flex-1">
            <p className="font-bold text-sm">{boost.name}</p>
            <p className="text-xs text-zinc-400">{boost.description}</p>
          </div>
          <div className="text-right mr-4">
            <p className="text-primary font-mono font-bold text-sm">{(boost.price || 0).toLocaleString()} CR</p>
            <p className="text-xs text-zinc-500">{effectLabel}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => startEdit(boost)} className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors" data-testid={`edit-boost-${boost.boost_id}`}>
            <Edit size={16} />
          </button>
          <button onClick={() => deleteBoost(boost.boost_id, boost.name)} className="p-2 text-zinc-400 hover:text-red-400 hover:bg-zinc-800 transition-colors" data-testid={`delete-boost-${boost.boost_id}`}>
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="boosts-tab">
      <div className="bg-card border border-zinc-800 p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="font-heading font-bold text-2xl flex items-center">
              <Zap className="mr-2 text-primary" size={24} />
              Boost Definitions
            </h2>
            <p className="text-zinc-400 text-sm mt-1">
              Manage boost items available in the Workshop. Changes apply immediately to the shop.
            </p>
          </div>
          <button onClick={startCreate} className="flex items-center gap-2 bg-primary hover:bg-primary/80 text-white px-4 py-2 font-bold" data-testid="add-boost-btn">
            <Plus size={18} /> Add Boost
          </button>
        </div>

        {creating && renderForm(true)}

        <div className="mb-6">
          <h3 className="font-heading font-bold text-lg text-primary mb-3">Racing Boosts ({racingBoosts.length})</h3>
          <div className="space-y-2">
            {racingBoosts.map(renderBoostRow)}
            {racingBoosts.length === 0 && <p className="text-zinc-500 text-sm p-3">No racing boosts defined</p>}
          </div>
        </div>

        <div>
          <h3 className="font-heading font-bold text-lg text-secondary mb-3">Moto Boosts ({motoBoosts.length})</h3>
          <div className="space-y-2">
            {motoBoosts.map(renderBoostRow)}
            {motoBoosts.length === 0 && <p className="text-zinc-500 text-sm p-3">No moto boosts defined</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
